import type { EndpointOverlay } from './overlay';

// Korean endpoint pages.

// Parameter descriptions shared across many endpoints. Endpoint-specific `params`
// entries override these.
const commonParams: Record<string, string> = {
  limit: '페이지당 결과 수.',
  sort: '정렬에 사용할 필드.',
  order: '정렬 방향: asc(오름차순) 또는 desc(내림차순).',
  adjusted: '권리락 조정 반영 여부. 기본값은 true.',
  ts_code: 'TS 증권 코드. 일부 데이터셋은 쉼표로 여러 코드를 지정할 수 있습니다.',
  trade_date: '거래일(YYYYMMDD 형식).',
  start_date: '시작일(당일 포함, YYYYMMDD 형식).',
  end_date: '종료일(당일 포함, YYYYMMDD 형식).',
  fields: '쉼표로 구분한 출력 필드. 생략하면 기본 필드를 반환합니다.',
};

const aggregateParams: Record<string, string> = {
  stocksTicker: '대소문자를 구분하는 티커 심볼.',
  optionsTicker: '표준 형식의 옵션 계약 티커.',
  multiplier: '시간 단위에 곱할 배수.',
  timespan: '집계 단위: minute, hour, day, week, month, quarter, year.',
  from: '구간 시작(YYYY-MM-DD 또는 Unix 밀리초 타임스탬프).',
  to: '구간 종료(YYYY-MM-DD 또는 Unix 밀리초 타임스탬프).',
  adjusted: '주식 분할 조정 반영 여부. 기본값은 true.',
  sort: '타임스탬프 정렬 방향: asc 또는 desc.',
  limit: '조회할 기초 집계 상한. 기본값 5,000, 최대 50,000.',
};

const tickParams: Record<string, string> = {
  order: '정렬 필드 기준 방향: asc 또는 desc.',
  limit: '페이지당 결과 수. 기본값 1,000, 최대 50,000.',
  sort: '정렬에 사용할 필드. 기본값은 timestamp.',
};

export const endpointsKo: Record<string, EndpointOverlay> = {
  'us-stocks-custom-bars': {
    title: '커스텀 바(OHLC)',
    summary: '원하는 구간의 과거 OHLC와 거래량.',
    description:
      '프리마켓, 정규장, 애프터마켓 세션을 포함한 미국 주식의 조정 가능한 집계 바를 가져옵니다. 특정 구간에 데이터가 없다면 해당 구간에 조건을 충족하는 체결이 없었다는 뜻입니다.',
    useCases: ['차트 작성', '기술적 분석', '백테스팅', '시장 리서치'],
    params: aggregateParams,
  },
  'us-options-custom-bars': {
    title: '커스텀 바(OHLC)',
    summary: '개별 옵션 계약의 과거 집계 바.',
    description:
      '지정한 옵션 계약의 원하는 구간 OHLC, 거래량, 체결 건수, VWAP를 가져옵니다. 표준 형식의 O: 계약 티커를 사용하세요.',
    useCases: ['옵션 차트', '변동성 리서치', '백테스팅', '계약 분석'],
    params: aggregateParams,
  },
  'us-options-trades': {
    title: '체결',
    summary: '옵션 계약의 틱 단위 체결 기록.',
    description:
      '지정한 옵션 계약의 특정 기간 틱 단위 체결 데이터를 가져옵니다. 각 레코드에는 가격, 수량, 거래소, 체결 조건, 정밀한 타임스탬프가 포함되어 장중 분석, 시장 미시구조 연구, 백테스팅, 감사 가능한 거래 이력에 활용할 수 있습니다.',
    useCases: ['장중 분석', '알고리즘 트레이딩', '시장 미시구조 연구', '데이터 무결성과 컴플라이언스'],
    notes: [
      '설정된 Options Advanced 업스트림에서 실시간으로 제공됩니다.',
      '전체 기록은 2014년 6월 2일까지 거슬러 올라갑니다.',
      '타임스탬프는 나노초 정밀도의 Unix 타임스탬프입니다.',
    ],
    params: {
      ...tickParams,
      optionsTicker: 'O: 접두사가 붙은 표준 옵션 계약 티커.',
      timestamp:
        '체결 타임스탬프로 조회합니다. YYYY-MM-DD 형식의 날짜 또는 나노초 타임스탬프를 쓸 수 있으며 .gt, .gte, .lt, .lte 수식어를 지원합니다.',
    },
  },
  'us-options-quotes': {
    title: '호가',
    summary: '옵션 계약의 과거 매수·매도 호가.',
    description:
      '지정한 옵션 계약의 특정 기간 과거 호가를 가져옵니다. 각 레코드에는 매수·매도 호가와 수량, 거래소 ID, 정밀한 타임스탬프가 포함되어 해당 시점의 옵션 시장 상황을 보여줍니다.',
    useCases: ['과거 호가 분석', '시장 관심도 평가', '알고리즘 백테스팅', '전략 개선'],
    notes: [
      '설정된 Options Advanced 업스트림에서 실시간으로 제공됩니다.',
      '전체 기록은 2022년 3월 7일까지 거슬러 올라갑니다.',
      '호가 수량은 단주(보통 100주) 단위로 표시됩니다.',
    ],
    params: {
      ...tickParams,
      optionsTicker: 'O: 접두사가 붙은 표준 옵션 계약 티커.',
      timestamp:
        '호가 타임스탬프로 조회합니다. YYYY-MM-DD 형식의 날짜 또는 나노초 타임스탬프를 쓸 수 있으며 .gt, .gte, .lt, .lte 수식어를 지원합니다.',
    },
  },
  'us-trades': {
    title: '체결',
    summary: '미국 주식의 틱 단위 체결 기록.',
    description:
      '지정한 미국 주식의 특정 기간 전체 틱 단위 체결 데이터를 가져옵니다. 각 레코드에는 가격, 수량, 거래소, 체결 조건, 나노초 정밀도의 타임스탬프가 포함됩니다. 이 수준의 데이터는 집계 바 구성과 시장 미시구조 분석의 토대가 되며, OHLC에 반영되는 모든 적격 체결을 포함합니다.',
    useCases: ['장중 분석', '알고리즘 트레이딩', '시장 미시구조 연구', '데이터 무결성과 컴플라이언스'],
    notes: [
      '최상위 요금제에서는 2003년 9월 10일까지 거슬러 조회할 수 있습니다.',
      '타임스탬프는 나노초 정밀도의 Unix 타임스탬프입니다.',
    ],
    params: {
      ...tickParams,
      stockTicker: '대소문자를 구분하는 주식 티커.',
      timestamp:
        '체결 타임스탬프로 조회합니다. YYYY-MM-DD 형식의 날짜 또는 나노초 타임스탬프를 쓸 수 있으며 .gt, .gte, .lt, .lte 수식어를 지원합니다.',
    },
  },
  'us-sma': {
    title: '단순이동평균(SMA)',
    summary: '지정한 구간의 단순이동평균.',
    description:
      '지정한 티커의 특정 기간 단순이동평균(SMA)을 가져옵니다. SMA는 일정 기간의 가격을 평균해 변동을 완만하게 만들어 기저 추세와 신호를 드러냅니다.',
    useCases: ['추세 분석', '매매 신호(SMA 교차)', '지지·저항 판단', '진입과 청산 시점'],
    notes: ['일간 집계에 구간 10을 지정하면 10일 이동평균이 됩니다.'],
    params: {
      stockTicker: '대소문자를 구분하는 주식 티커.',
      timestamp:
        '타임스탬프로 조회합니다. YYYY-MM-DD 형식의 날짜 또는 밀리초 타임스탬프를 쓸 수 있으며 .gt, .gte, .lt, .lte 수식어를 지원합니다.',
      timespan: '집계 단위: minute, hour, day, week, month, quarter, year.',
      adjusted: '사용하는 집계에 주식 분할 조정을 반영할지 여부. 기본값은 true.',
      window: 'SMA를 계산할 구간 크기. 기본값은 50.',
      series_type: '각 집계에서 사용할 가격: open, high, low, close. 기본값은 close.',
      expand_underlying: '계산에 사용한 집계 데이터를 응답에 포함할지 여부.',
      order: '타임스탬프 정렬 방향: asc 또는 desc. 기본값은 desc.',
      limit: '페이지당 결과 수. 기본값 10, 최대 5,000.',
    },
  },
  'us-news': {
    title: '뉴스',
    summary: '감성 분석이 포함된 최신 뉴스.',
    description:
      '지정한 티커와 관련된 최신 뉴스 기사를 요약, 출처 정보, 감성 분석과 함께 가져옵니다. 금융 뉴스를 모아 관련 티커를 추출하고 감성을 표시한 뒤 원문으로 연결해 주므로 시장 심리와 기업 동향을 파악하는 데 유용합니다.',
    useCases: ['시장 심리 분석', '투자 리서치', '자동 모니터링', '포트폴리오 전략 최적화'],
    params: {
      ticker: '대소문자를 구분하는 주식 티커. 필터 수식어를 지원합니다.',
      published_utc:
        '지정한 일시에 게시되었거나 그 이전·이후의 결과를 반환합니다. .gt, .gte, .lt, .lte 수식어를 지원합니다.',
      order: '정렬 필드 기준 방향: asc 또는 desc.',
      limit: '페이지당 결과 수. 기본값 10, 최대 1,000.',
      sort: '정렬에 사용할 필드. 기본값은 published_utc.',
    },
  },
  'us-options-contracts': {
    title: '전체 계약',
    summary: '유효 계약과 만기 계약의 인덱스.',
    description:
      '유효 계약과 만기된 계약을 아우르는 전체 인덱스를 가져옵니다. 시장 전체를 조회하거나 특정 기초자산 종목으로 한정할 수 있습니다. 각 레코드에는 계약 유형(콜/풋), 행사 방식, 만기일, 행사가가 포함되어 시장 공급 현황 분석과 옵션 전략 연구에 활용할 수 있습니다.',
    useCases: ['공급 현황 분석', '전략 개발', '리서치와 모델링', '계약 탐색'],
    notes: ['더 이상 지원되지 않는 ticker 파라미터는 사용할 수 없습니다. 개별 계약 조회에는 옵션 계약 엔드포인트를 사용하세요.'],
    params: {
      underlying_ticker: '특정 기초자산 종목과 연결된 계약을 조회합니다. 필터 수식어를 지원합니다.',
      contract_type: '계약 유형으로 필터링합니다: call, put, other.',
      expiration_date: '만기일(YYYY-MM-DD)로 필터링합니다. 필터 수식어를 지원합니다.',
      as_of: '지정한 날짜(YYYY-MM-DD) 기준 계약 상태. 기본값은 오늘.',
      strike_price: '행사가로 필터링합니다. 필터 수식어를 지원합니다.',
      expired: '만기된 계약을 조회합니다. 기본값은 false.',
      order: '정렬 필드 기준 방향: asc 또는 desc.',
      limit: '페이지당 결과 수. 기본값 10, 최대 1,000.',
      sort: '정렬에 사용할 필드. 기본값은 ticker.',
    },
  },
  'us-balance-sheets': {
    title: '재무상태표',
    summary: '분기·연간 재무 상태 스냅샷.',
    description:
      '상장기업이 제출한 자산, 부채, 자본을 표준화된 형태로 가져옵니다. 각 레코드는 특정 회계기간 시점의 스냅샷이며 기간 누적 흐름이 아닙니다.',
    useCases: ['펀더멘털 분석', '밸류에이션', '부채 분석', '개별 종목 리서치'],
    params: {
      cik: 'SEC 중앙 색인 코드(CIK).',
      tickers: '티커 배열에 이 티커가 포함된 레코드로 필터링합니다.',
      period_end: '기말일(YYYY-MM-DD). .gt, .gte, .lt, .lte를 붙일 수 있습니다.',
      filing_date: '해당 기간을 포함하는 최근 SEC 제출일. 비교 수식어를 지원합니다.',
      fiscal_year: '회계연도. .gt, .gte, .lt, .lte를 붙일 수 있습니다.',
      fiscal_quarter: '회계 분기(1~4).',
      timeframe: '보고 주기: quarterly 또는 annual.',
      limit: '페이지당 결과 수. 기본값 100, 최대 50,000.',
      sort: '쉼표로 구분한 필드에 .asc 또는 .desc를 붙여 지정합니다.',
    },
  },
  'us-financial-ratios': {
    title: '재무 비율',
    summary: '밸류에이션, 수익성, 유동성, 레버리지 지표.',
    description:
      '최근 거래일을 기준으로 최근 12개월(TTM) 재무 데이터와 일간 시장 가격을 사용해 산출한 기업 비율을 가져옵니다.',
    useCases: ['기업 밸류에이션', '동종업계 비교', '재무 건전성', '종목 스크리닝'],
    notes: ['숫자형 조회 필드는 .gt, .gte, .lt, .lte 같은 비교 수식어를 지원합니다.'],
    params: {
      ticker: '주식 티커. 필터 수식어를 지원합니다.',
      cik: 'SEC 중앙 색인 코드(CIK). 필터 수식어를 지원합니다.',
      price: '산출에 사용한 주가.',
      average_volume: '최근 30 거래일 평균 거래량.',
      market_cap: '시가총액.',
      earnings_per_share: '보통주 주주 귀속 가중평균 주당순이익.',
      price_to_earnings: '주가를 양(+)의 주당순이익으로 나눈 값.',
      price_to_book: '주가를 주당 순자산으로 나눈 값.',
      price_to_sales: '주가를 주당 매출로 나눈 값.',
      price_to_cash_flow: '주가를 양(+)의 주당 영업현금흐름으로 나눈 값.',
      price_to_free_cash_flow: '주가를 양(+)의 주당 잉여현금흐름으로 나눈 값.',
      dividend_yield: '주당 연간 배당금을 주가로 나눈 값.',
      return_on_assets: '순이익을 총자산으로 나눈 값.',
      return_on_equity: '순이익을 자기자본으로 나눈 값.',
      debt_to_equity: '단기·장기 부채를 자기자본으로 나눈 값.',
      current: '유동자산을 유동부채로 나눈 값.',
      quick: '유동자산에서 재고자산을 뺀 뒤 유동부채로 나눈 값.',
      cash: '현금 및 현금성자산을 유동부채로 나눈 값.',
      ev_to_sales: '기업가치를 매출로 나눈 값.',
      ev_to_ebitda: '기업가치를 EBITDA로 나눈 값.',
      enterprise_value: '시가총액에 부채를 더하고 현금 및 현금성자산을 뺀 값.',
      free_cash_flow: '영업현금흐름에서 자본적지출을 뺀 값.',
      limit: '페이지당 결과 수. 기본값 100, 최대 50,000.',
      sort: '쉼표로 구분한 필드에 .asc 또는 .desc를 붙여 지정합니다. 기본값은 ticker.asc.',
    },
  },
  'cn-daily': {
    title: '일간 시세',
    summary: '미수정 A주 일간 OHLC와 거래 현황.',
    description:
      '장 마감 후 공개되는 A주 일간 데이터입니다. 거래정지 종목은 행이 없습니다. 가격은 수정되지 않았으므로 필요하면 수정계수 데이터셋과 함께 사용하세요.',
    useCases: ['일간 스크리닝', '과거 분석', '시장 폭', '백테스팅'],
    notes: [
      '전체 시장을 수집하려면 종목 코드가 아니라 거래일 단위로 반복 조회하세요.',
      '날짜는 YYYYMMDD 형식을 사용합니다.',
    ],
    params: commonParams,
  },
  'cn-cyq-perf': {
    title: '매물대 원가와 수익 비율',
    summary: '일간 보유 원가 분포와 수익 비율.',
    description:
      '일간 보유 원가 분위수, 과거 범위, 가중평균 원가, 평가이익 구간의 보유 비율을 가져옵니다. 데이터는 2018년부터 제공됩니다.',
    useCases: ['원가 분석', '수급 신호', '지지·저항', '일간 스크리닝'],
    notes: ['호출당 최대 6,000행입니다. 페이지를 나누거나 조회 기간을 좁히세요.'],
    params: { ...commonParams, ts_code: 'TS 증권 코드(필수).' },
  },
  'cn-technical-factors': {
    title: '기술적 팩터',
    summary: '일간 수정 가격과 퀀트 기술적 지표.',
    description:
      'A주를 대상으로 생성한 전체 기간 일간 가격 팩터와 기술적 지표를 가져옵니다. 지표는 전방 수정 가격을 기준으로 계산됩니다.',
    useCases: ['팩터 연구', '신호 생성', '추세 분석', '퀀트 종목 선정'],
    notes: [
      '호출당 최대 10,000행입니다. 페이지를 나누거나 조회 범위를 좁히세요.',
      '전방 수정 이력은 최근 거래일을 기준으로 한 일간 스냅샷이며 소급해 갱신되지 않습니다.',
      '동적 pro_bar 수정은 요청한 end_date를 기준으로 하므로 결과가 다를 수 있습니다.',
      '기술적 지표는 전방 수정 가격을 기준으로 계산됩니다.',
    ],
    params: commonParams,
  },
  'cn-balance-sheet': {
    title: '재무상태표',
    summary: '개별 A주 상장기업이 제출한 재무상태표.',
    description:
      '상장기업의 재무상태표 항목 이력을 가져옵니다. 일반 기업, 은행, 보험사, 증권사의 보고 양식을 모두 지원합니다.',
    useCases: ['펀더멘털 분석', '지급능력 조사', '분기 스크리닝', '기업 밸류에이션'],
    notes: ['요청당 한 기업만 조회할 수 있습니다. 전체 시장 분기 데이터는 날짜 중심의 상위 데이터셋을 사용하세요.'],
    params: {
      ts_code: 'TS 증권 코드. 이 데이터셋은 요청당 한 기업만 조회합니다.',
      ann_date: '공시일(YYYYMMDD 형식).',
      start_date: '공시일 범위 시작.',
      end_date: '공시일 범위 종료.',
      period: '보고 기말. 예: 20251231은 연간 보고서.',
      report_type: '보고서 유형.',
      comp_type: '기업 구분: 1 일반, 2 은행, 3 보험, 4 증권.',
      fields: '쉼표로 구분한 출력 필드.',
    },
  },
  'cn-disclosure-date': {
    title: '공시 일정',
    summary: '정기 보고서의 예정·실제 공시일.',
    description:
      '정기 보고서의 공시 일정을 가져옵니다. 예정 공시일, 최근 공고일, 각 보고 기간의 실제 공시일이 포함되어 실적 발표 예측과 이벤트 캘린더 구성에 적합합니다.',
    useCases: ['실적 캘린더', '이벤트 드리븐 전략', '커버리지 추적', '실적 시즌 계획'],
    notes: ['호출당 최대 6,000행입니다.', '날짜는 YYYYMMDD 형식을 사용합니다.'],
    params: {
      ts_code: 'TS 증권 코드.',
      end_date: '보고 기말(YYYYMMDD 형식. 예: 20181231은 2018년 연간 보고서, 20180630은 반기 보고서).',
      pre_date: '예정 공시일(YYYYMMDD 형식).',
      ann_date: '최근 공시 공고일(YYYYMMDD 형식).',
      actual_date: '실제 공시일(YYYYMMDD 형식).',
    },
  },
  'indices-custom-bars': {
    title: '커스텀 바(OHLC)',
    summary: '임의 구간으로 집계한 지수의 과거 값.',
    description:
      '지수에 대해 원하는 기간과 시간 단위(미 동부시간)로 집계한 시가, 고가, 저가, 종가를 가져옵니다. 주식이나 옵션과 달리 지수 집계는 개별 체결이 아니라 공표된 지수 값에서 산출되므로, 새로운 지수 값이 없는 구간에는 바가 생성되지 않습니다.',
    useCases: ['벤치마크 비교', '시장 추세 분석', '데이터 시각화', '리서치와 모델링'],
    params: {
      indicesTicker: '표준 I: 접두사가 붙은 지수 티커.',
      multiplier: '시간 단위에 곱할 배수.',
      timespan: '집계 단위: minute, hour, day, week, month, quarter, year.',
      from: '구간 시작(YYYY-MM-DD 또는 Unix 밀리초 타임스탬프).',
      to: '구간 종료(YYYY-MM-DD 또는 Unix 밀리초 타임스탬프).',
      sort: '타임스탬프 정렬: asc 또는 desc.',
      limit: '조회할 기초 집계 수 상한. 기본값 5,000, 최대 50,000.',
    },
    notes: [
      '지수 티커는 표준 I: 접두사를 사용하며, 요청은 자동으로 지수 이용 권한으로 라우팅됩니다.',
      '지수 바는 체결로 만들어지지 않으므로 거래량, VWAP, 체결 건수가 없습니다.',
    ],
  },
  'indices-previous-day-bar': {
    title: '전 거래일 바',
    summary: '지수의 직전 거래일 OHLC.',
    description:
      '지수의 직전 거래일 시가, 고가, 저가, 종가를 가져옵니다. 날짜 범위를 직접 계산하지 않고도 직전 세션 기준값을 바로 확보할 수 있습니다.',
    useCases: ['기준값 비교', '일일 리포트', '기술적 분석', '시장 조사'],
    params: {
      indicesTicker: '표준 I: 접두사가 붙은 지수 티커.',
    },
    notes: ['"전 거래일"은 가장 최근에 종료된 세션을 뜻하며 장중에는 변하지 않습니다.'],
  },
  'indices-daily-summary': {
    title: '일간 요약',
    summary: '특정 날짜의 지수 시가와 종가.',
    description:
      '지정한 날짜의 지수 시가와 종가, 해당 세션의 고가와 저가, 그리고 프리마켓·애프터마켓에서 산출된 값을 가져옵니다. 정규장 밖에서도 값을 공표하는 산출 방식을 가진 지수에 한해 해당 필드가 반환됩니다.',
    useCases: ['일간 성과 분석', '과거 데이터 수집', '시간외 파악', '포트폴리오 추적'],
    params: {
      indicesTicker: '표준 I: 접두사가 붙은 지수 티커.',
      date: '세션 날짜(YYYY-MM-DD).',
    },
    notes: ['지수 정규 거래시간은 미 동부시간 월~금 09:30~16:00입니다.'],
  },
  'indices-snapshot': {
    title: '지수 스냅샷',
    summary: '여러 지수의 현재 값과 세션 성과를 한 번에 조회.',
    description:
      '여러 지수의 스냅샷을 가져옵니다. 현재 값, 당일 시가·고가·저가, 전일 종가, 전일 대비 변화액과 변화율이 포함됩니다. 한 번의 요청으로 관심 목록 전체를 처리할 수 있어, 지수마다 개별 폴링 없이 최신 상태를 유지해야 하는 대시보드에 적합합니다.',
    useCases: ['시장 상황 파악', '시장 심리 추적', '대시보드 타일', '포트폴리오 맥락'],
    params: {
      'ticker.any_of': '쉼표로 구분한 지수 티커 목록.',
      ticker: '단일 지수 티커. .gt, .gte, .lt, .lte 범위 수식어를 지원합니다.',
      order: '정렬 방향: asc 또는 desc.',
      limit: '페이지당 결과 수. 기본값 10, 최대 250.',
      sort: '정렬 기준 필드. 기본값은 ticker.',
    },
    notes: ['ticker.any_of를 사용하면 한 번의 요청으로 관심 목록 전체를 조회할 수 있습니다.'],
  },
  'indices-tickers': {
    title: '전체 지수 티커',
    summary: '검색 가능한 지수 심볼 디렉터리.',
    description:
      'Eroya가 지원하는 지수 심볼을 지수명, 산출 기관, 활성 여부와 함께 가져옵니다. 공용 티커 디렉터리를 market=indices로 좁히면 지수만 조회되며, S&P, Nasdaq, 다우존스, FTSE Russell, MSCI, 모닝스타, Cboe Global Indices, 소시에테 제네랄 등 10,000개가 넘는 지수를 포괄합니다.',
    useCases: ['지수 탐색', '심볼 검증', '관심 목록 구성', '데이터 연동'],
    params: {
      market: '지수를 조회하려면 indices로 지정합니다.',
      ticker: '특정 지수 심볼을 지정합니다. .gt, .gte, .lt, .lte를 지원합니다.',
      search: '지수명에 대한 대소문자 구분 없는 부분 일치 검색.',
      active: '현재도 산출되는지 여부. 기본값은 true.',
      order: '정렬 방향: asc 또는 desc.',
      limit: '페이지당 결과 수. 기본값 100, 최대 1,000.',
      sort: '정렬 기준 필드. 기본값은 ticker.',
    },
    notes: ['요청을 지수 이용 권한으로 라우팅하는 것은 market=indices입니다. 지정하지 않으면 주식 티커가 반환됩니다.'],
  },
  'us-income-statements': {
    title: '손익계산서',
    summary: '분기·연간 매출, 비용, 이익 흐름.',
    description:
      '상장기업이 제출한 손익계산서를 표준화된 형태로 가져옵니다. 재무상태표와 달리 이 레코드는 회계기간 동안의 흐름이므로, 같은 연도의 분기 레코드와 연간 레코드는 서로 다른 구간을 나타내며 하나의 시계열에 섞어 쓸 수 없습니다.',
    useCases: ['펀더멘털 분석', '실적 추세 리서치', '마진 분석', '스크리닝과 모델링'],
    params: {
      cik: 'SEC 중앙 색인 코드(CIK).',
      tickers: '티커 배열에 이 티커가 포함된 레코드로 필터링합니다.',
      period_end: '기말일(YYYY-MM-DD). .gt, .gte, .lt, .lte를 붙일 수 있습니다.',
      filing_date: '해당 기간을 포함하는 최근 SEC 제출일. 비교 수식어를 지원합니다.',
      fiscal_year: '회계연도. .gt, .gte, .lt, .lte를 붙일 수 있습니다.',
      fiscal_quarter: '회계 분기(1~4).',
      timeframe: '보고 주기: quarterly 또는 annual.',
      limit: '페이지당 결과 수. 기본값 100, 최대 50,000.',
      sort: '쉼표로 구분한 필드에 .asc 또는 .desc를 붙여 지정합니다.',
    },
    notes: ['분기와 연간은 대상 구간이 다릅니다. 시계열을 만들기 전에 timeframe으로 먼저 필터링하세요.'],
  },
  'us-cash-flow-statements': {
    title: '현금흐름표',
    summary: '기간별 영업·투자·재무 현금흐름.',
    description:
      '상장기업이 제출한 현금흐름표를 표준화된 형태로 가져옵니다. 현금흐름은 회계정책의 영향을 가장 덜 받는 재무제표이므로 보고 이익의 질을 교차 검증하는 데 널리 쓰입니다.',
    useCases: ['이익의 질 점검', '잉여현금흐름 모델링', '자본 배분 리서치', '밸류에이션'],
    params: {
      cik: 'SEC 중앙 색인 코드(CIK).',
      tickers: '티커 배열에 이 티커가 포함된 레코드로 필터링합니다.',
      period_end: '기말일(YYYY-MM-DD). .gt, .gte, .lt, .lte를 붙일 수 있습니다.',
      filing_date: '해당 기간을 포함하는 최근 SEC 제출일. 비교 수식어를 지원합니다.',
      fiscal_year: '회계연도. .gt, .gte, .lt, .lte를 붙일 수 있습니다.',
      fiscal_quarter: '회계 분기(1~4).',
      timeframe: '보고 주기: quarterly 또는 annual.',
      limit: '페이지당 결과 수. 기본값 100, 최대 50,000.',
      sort: '쉼표로 구분한 필드에 .asc 또는 .desc를 붙여 지정합니다.',
    },
  },
  'us-short-interest': {
    title: '공매도 잔고',
    summary: '월 2회 보고되는 공매도 잔고와 커버 소요일수.',
    description:
      '결제일 기준으로 보고된 종목별 공매도 잔고와, 커버 소요일수 계산에 사용된 평균 거래량을 가져옵니다. 거래소는 이 잔고를 월 2회 집계하므로 이 계열은 일간 지표가 아니라 결제일 시점의 스냅샷입니다.',
    useCases: ['쏠림·숏스퀴즈 분석', '심리 리서치', '리스크 스크리닝', '이벤트 스터디'],
    params: {
      ticker: '종목 티커. 필터 수식어를 지원합니다.',
      settlement_date: '결제일(YYYY-MM-DD). .gt, .gte, .lt, .lte를 붙일 수 있습니다.',
      days_to_cover: '커버 소요일수로 필터링합니다. 비교 수식어를 지원합니다.',
      limit: '페이지당 결과 수. 기본값 100, 최대 50,000.',
      sort: '쉼표로 구분한 필드에 .asc 또는 .desc를 붙여 지정합니다. 기본값은 ticker.asc.',
    },
    notes: ['잔고는 월 2회 보고되며 결제일보다 며칠 늦게 공표됩니다.'],
  },
  'us-short-volume': {
    title: '공매도 거래량',
    summary: '일간 공매도 거래량과 총거래량 대비 비중.',
    description:
      '종목별 일간 공매도 거래량을 비교 기준이 되는 총거래량과 함께 가져오므로 일간 공매도 비율을 바로 계산할 수 있습니다. 이는 매매 활동 지표이며, 월 2회 보고되는 결제일 기준 공매도 잔고와는 다릅니다.',
    useCases: ['일간 심리 추적', '자금 흐름 분석', '시그널 구성', '유동성 리서치'],
    params: {
      ticker: '종목 티커. 필터 수식어를 지원합니다.',
      date: '거래일(YYYY-MM-DD). .gt, .gte, .lt, .lte를 붙일 수 있습니다.',
      short_volume_ratio: '공매도 비율로 필터링합니다. 비교 수식어를 지원합니다.',
      limit: '페이지당 결과 수. 기본값 100, 최대 50,000.',
      sort: '쉼표로 구분한 필드에 .asc 또는 .desc를 붙여 지정합니다. 기본값은 ticker.asc.',
    },
    notes: ['공매도 거래량 비율에는 장중 마켓메이킹도 포함되므로 그 자체로 약세 신호는 아닙니다.'],
  },
  'flatfiles-datasets': {
    title: '플랫 파일 데이터셋 목록',
    summary: '모든 플랫 파일 데이터셋과 요금제 이용 권한.',
    description:
      '플랫 파일 카탈로그를 반환합니다. 다른 플랫 파일 엔드포인트에서 쓸 수 있는 데이터셋 ID, 아카이브 경로, 데이터 단위, 이용에 필요한 최소 요금제, 현재 키로 접근 가능한지 여부가 포함됩니다. 다운로드 자체에는 사용량 제한이 없습니다 — 요금제는 어떤 데이터셋에 접근할 수 있는지만 정하며 바이트 단위로 과금하지 않습니다.',
    useCases: ['데이터셋 탐색', '권한 확인', '파이프라인 설정', '요금제 비교'],
    notes: [
      '일간 및 분봉 데이터셋은 Pro 요금제부터, 틱 체결과 호가는 Premium 요금제부터 이용할 수 있습니다.',
      'dataset 파라미터에는 데이터셋 ID와 전체 아카이브 경로를 모두 사용할 수 있습니다.',
    ],
  },
  'flatfiles-list': {
    title: '플랫 파일 목록',
    summary: '지정한 기간에 공개된 파일과 크기.',
    description:
      '지정한 기간에 데이터셋에서 공개된 플랫 파일을 각 객체의 실제 바이트 크기와 함께 나열합니다. 실제로 거래가 있었던 거래일만 포함되므로, 내려받기 전에 대상 날짜를 파악하고 전송량을 미리 가늠하는 확실한 방법입니다.',
    useCases: ['과거 데이터 보충 계획', '전송량 추정', '증분 동기화', '누락 감지'],
    params: {
      dataset: '데이터셋 ID 또는 아카이브 경로.',
      start: '구간 시작일(당일 포함, YYYY-MM-DD 형식).',
      end: '구간 종료일(당일 포함, YYYY-MM-DD 형식). 생략하면 start와 같습니다.',
    },
    notes: [
      '한 번의 조회에서 지정할 수 있는 기간은 366일까지입니다.',
    ],
  },
  'flatfiles-url': {
    title: '사전 서명 다운로드 URL 발급',
    summary: '하루치 파일에 대한 단기 유효 직접 링크.',
    description:
      '하루치 파일에 대해 사전 서명된 HTTPS URL을 반환합니다. 표준 S3 서명 URL이므로 모든 HTTP 클라이언트, S3 클라이언트, 데이터프레임 라이브러리에서 바로 읽을 수 있고 Range 요청도 지원합니다. Eroya는 링크만 발급하고 파일 본문을 중계하지 않으므로 전송 속도는 사용자의 회선에 달려 있습니다.',
    useCases: ['직접 다운로드', 'Polars·DuckDB 부분 읽기', '과거 데이터 병렬 보충', '데이터 웨어하우스 적재'],
    params: {
      dataset: '데이터셋 ID 또는 아카이브 경로.',
      date: '거래일(YYYY-MM-DD 형식).',
      expiresIn: '링크 유효 시간(초). 60~3600, 기본값 900.',
    },
    notes: [
      '각 링크는 유효 기간 동안 파일 하나만 허용합니다. 필요할 때 다시 발급받고 장기 보관은 권장하지 않습니다.',
      '각 거래일 파일은 다음 거래일 미 동부시간 오전 11시경에 공개됩니다. 그보다 일찍 요청하면 404가 반환됩니다.',
    ],
  },
  'flatfiles-download': {
    title: '플랫 파일 다운로드',
    summary: '서명된 파일로 바로 리디렉션합니다.',
    description:
      '사전 서명 URL 엔드포인트와 동작은 같지만, JSON을 반환하는 대신 HTTP 302로 서명된 URL로 바로 리디렉션합니다. 리디렉션을 따라가는 클라이언트라면 Eroya URL을 파일 자체처럼 다룰 수 있어 서명 처리를 직접 구현할 필요가 없습니다.',
    useCases: ['curl과 wget', '셸 파이프라인', 'pandas 직접 읽기', 'Airflow·cron 스케줄링'],
    params: {
      dataset: '데이터셋 ID 또는 아카이브 경로.',
      date: '거래일(YYYY-MM-DD 형식).',
      expiresIn: '링크 유효 시간(초). 60~3600, 기본값 900.',
    },
    notes: [
      'curl에는 -L을, requests에는 allow_redirects를 사용해 클라이언트가 리디렉션을 따르도록 하세요.',
      '리디렉션 대상은 서명된 단일 오브젝트입니다. 저장하지 말고 즉시 따라가세요.',
    ],
  },
};
